import { Component, OnInit, ViewChild, OnDestroy, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { distinctUntilChanged, map, takeUntil, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { CustomTableComponent } from '../../../../shared/ui/custom-table/components/table/custom-table.component';
import { TableConfig } from '../../../../shared/ui/custom-table/model/table-config.model';
import { NotificationService } from '../../../shared/services/notification.service';
import { mapTableState, tableStateEquals } from '../../../shared/utils/table-state.utils';
import { BookingActivationGuardService } from '../../services/booking-activation-guard.service';
import { BookingService, ListBookingsFilter } from '../../services/booking.service';
import { createBookingTableConfig, BookingTableRowAction } from './booking-table.config';
import { ConfirmDialogV2, ConfirmDialogDataV2 } from '../../../shared/components/confirm-dialog.component';
import { formatLocalIso } from '../../utils/date.utils';

@Component({
    selector: 'app-booking-list',
    templateUrl: './booking-list.component.html',
    styleUrls: ['./booking-list.component.css'],
    standalone: false
})
export class BookingListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('table', {static: false}) table!: CustomTableComponent;
  tableConfig!: TableConfig;

  icon = 'event';
  title = 'Reservas';
  subTitle = 'Consulta y gestiona las reservas de tus centros de trabajo';

  private destroy$ = new Subject<void>();

  constructor(
    private bookingActivationGuard: BookingActivationGuardService,
    private bookingService: BookingService,
    private router: Router,
    private notify: NotificationService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    // Inicializar la tabla inmediatamente para mostrar el loading
    this.tableConfig = createBookingTableConfig({
      onRowDblClick: (row) => this.viewBooking(row),
      actionMap: {
        [BookingTableRowAction.View]: (row) => this.viewBooking(row),
        [BookingTableRowAction.Edit]: (row) => this.editBooking(row),
        [BookingTableRowAction.Cancel]: (row) => this.cancelBooking(row),
      },
      columns: [
        { key: 'customerName', label: 'Cliente', sortable: false },
        { key: 'conceptName', label: 'Servicio', sortable: false },
        { key: 'staffName', label: 'Profesional', sortable: false },
        { key: 'startAt', label: 'Inicio', sortable: false },
        { key: 'endAt', label: 'Fin', sortable: false },
        { key: 'statusName', label: 'Estado', sortable: false },
      ],
    });

    // Verificar la suscripción de booking (redirige si no está activa)
    this.bookingActivationGuard.verifyAndRedirectIfInactive()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result === true) {
          // La verificación pasó, la tabla ya está inicializada
        }
      });
  }

  viewBooking(row: any): void {
    this.router.navigate(['/booking/bookings', row.bookingId]);
  }

  editBooking(row: any): void {
    this.router.navigate(['/booking/bookings/edit', row.bookingId]);
  }

  cancelBooking(row: any): void {
    const dialogRef = this.dialog.open(ConfirmDialogV2, {
      width: '420px',
      maxWidth: '96vw',
      data: {
        title: 'Cancelar reserva',
        message: `¿Seguro que deseas cancelar la reserva${row.customerName ? ' de ' + row.customerName : ''}? Esta acción cambiará el estado a cancelado.`,
        confirmText: 'Cancelar reserva',
        cancelText: 'Volver',
        color: 'warn',
        icon: 'cancel'
      } as ConfirmDialogDataV2
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(confirmed => {
      if (!confirmed) return;

      this.bookingService.cancel(row.bookingId).pipe(takeUntil(this.destroy$)).subscribe({
        next: (res) => {
          if (res && res.isSuccess) {
            this.notify.ok('Reserva cancelada');
            this.loadBookings(this.buildDefaultFilter());
          } else {
            this.notify.error('No se pudo cancelar la reserva');
          }
        },
        error: () => this.notify.error('Error al cancelar la reserva')
      });
    });
  }

  private buildDefaultFilter(page = 1, pageSize = 10): ListBookingsFilter {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    return {
      from: formatLocalIso(from),
      to: formatLocalIso(to),
      page,
      pageSize,
    };
  }

  private loadBookings(filter: ListBookingsFilter): void {
    if (!this.table || !this.table.tableState) return;
    this.table.tableState.setLoading(true);

    this.bookingService.list(filter).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.table.tableState.setData(res && res.values ? res.values : []);
        this.table.tableState.setTotalItems(res ? res.total : 0);
        this.table.tableState.setLoading(false);
      },
      error: () => {
        this.table.tableState.setLoading(false);
        this.notify.error('No se pudieron cargar las reservas');
      }
    });
  }

  ngAfterViewInit(): void {
    if (!this.table || !this.table.tableState) {
      console.error('[BookingListComponent] Table not initialized');
      return;
    }

    this.table.tableState.state$.pipe(
      map(mapTableState),
      distinctUntilChanged(tableStateEquals),
      tap((state) => {
        localStorage.setItem('bookings', JSON.stringify(state));
        this.loadBookings(this.buildDefaultFilter(state.pageIndex + 1, state.pageSize));
        this.table.tableState.setLoading(true);
      }),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
